'use client';

import { useToggleFollowCreator } from '@/api/mutations';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { useAuthStore } from '@/shared/stores/auth-store';
import { cn } from '@/shared/utils/formatting';
import type { Creator } from '@/types';
import { Check, Loader2 } from 'lucide-react';
import posthog from 'posthog-js';

export interface CreatorFollowButtonProps {
  creator: Creator;
  size?: 'sm' | 'lg';
  className?: string;
}

export function CreatorFollowButton({ creator, size = 'sm', className }: CreatorFollowButtonProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const toggleFollowMutation = useToggleFollowCreator();

  const isFollowing = creator.following ?? false;
  const isPending = toggleFollowMutation.isPending;

  const handleFollow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isPending) return;

    posthog.capture(
      isFollowing ? AnalyticsEvent.CREATOR_UNFOLLOWED : AnalyticsEvent.CREATOR_FOLLOWED,
      {
        creator_id: creator.id,
        creator_name: creator.name,
      }
    );

    toggleFollowMutation.mutate(
      { creatorId: creator.id, currentFollowing: isFollowing },
      {
        onError: (err) => {
          posthog.capture(AnalyticsEvent.FOLLOW_TOGGLE_ERROR, {
            creator_id: creator.id,
            creator_name: creator.name,
            error: err.message,
          });
        },
      }
    );
  };

  if (!isAuthenticated) return null;

  const sizeStyles = {
    sm: 'btn btn-sm min-w-24 rounded',
    lg: 'btn btn-sm px-8 py-3 text-sm font-semibold rounded min-w-[140px]',
  };

  const followingStyles = isFollowing
    ? 'bg-secondary text-white/80 hover:border-white/50 hover:text-white hover:bg-white/10'
    : size === 'lg'
      ? 'bg-red-primary text-white hover:scale-105 hover:shadow-glow-button'
      : 'btn-primary';

  return (
    <button
      onClick={handleFollow}
      aria-pressed={isFollowing}
      disabled={isPending}
      className={cn(
        'flex items-center justify-center gap-2 transition-all duration-200',
        sizeStyles[size],
        followingStyles,
        className
      )}
    >
      {isPending ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isFollowing ? (
        <>
          <Check className="w-4 h-4" />
          Following
        </>
      ) : (
        'Follow'
      )}
    </button>
  );
}
